<template>
	<div>
		<ValidationObserver
			ref="resetForm"
			v-slot="{ invalid, errors }"
			class="sign-in"
			tag="form"
			@submit.prevent="onReset"
			novalidate
		>
			<h2 v-html="$t('account.resetPassword.title')"/>

			<GeneralMessage
				v-if="resetResponse.message"
				:message="resetResponse.message"
				:type="resetResponse.type"
				@close="resetResponse.message = ''"
			/>

			<ValidationProvider
				v-if="!!isLoggedIn"
				name="currentPassword"
				rules="required"
				v-slot="{ errors, invalid, validated, dirty }"
				tag="div"
				class="input-wrapper"
				:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
			>
				<FormInput
					type="password"
					autocomplete="current-password"
					:placeholder="$t('forms.labels.currentPassword')"
					:label="$t('forms.labels.currentPassword')"
					v-model="form.current"
					:error="invalid && validated"
					:disabled="disabled"
					required
				/>
				<span
					class="input-error"
					v-if="invalid && validated"
					v-html="errors[0]"
				/>
			</ValidationProvider>

			<!-- PASSWORD -->
			<ValidationProvider
				class="input-wrapper"
				name="password"
				rules="required|complexpw"
				vid="password"
				v-slot="{ errors, invalid, validated, dirty }"
				tag="div"
				:customMessages="{ required: $t('forms.errors.fieldEmpty'), complexpw: $t('forms.errors.password') }"
			>
				<FormInput
					type="password"
					autocomplete="new-password"
					:placeholder="$t('forms.labels.newPassword')"
					:label="$t('forms.labels.newPassword')"
					v-model="form.password"
					:error="invalid && validated"
					:disabled="disabled"
					required
				/>
				<span
					class="input-error"
					v-if="invalid && validated"
					v-html="errors[0]"
				/>
			</ValidationProvider>

			<!-- PASSWORD CONFIRM -->
			<ValidationProvider
				class="input-wrapper"
				name="confirm"
				vid="confirm"
				rules="required|confirmed:password"
				v-slot="{ errors, invalid, validated, dirty }"
				tag="div"
				:customMessages="{ required: $t('forms.errors.fieldEmpty'), confirmed: $t('forms.errors.passwordMatch') }"
			>
				<FormInput
					type="password"
					autocomplete="new-password"
					:placeholder="$t('forms.labels.newPasswordRepeat')"
					:label="$t('forms.labels.newPasswordRepeat')"
					v-model="form.confirm"
					:error="invalid && validated"
					:disabled="disabled"
					required
				/>
				<span
					class="input-error"
					v-if="invalid && validated"
					v-html="errors[0]"
				/>
			</ValidationProvider>

			<Button
				type="submit"
				level="primary"
				:text="$t('account.resetPassword.resetButton')"
				:disabled="disabled"
			/>
		</ValidationObserver>
	</div>
</template>

<script>
import Vue from "vue"
import GenericMixin from '@/mixins/GenericVueMixin';
import OverlayMixin from '@/mixins/OverlayVueMixin';
import { mapGetters } from "vuex"
import { ValidationProvider, ValidationObserver } from "vee-validate"
import FormInput from "@/components/FormInput"
import FormInputCheckbox from "@/components/FormInputCheckbox"
import Button from '@/components/Button'
import GeneralMessage from '@/components/GeneralMessage'
import htmlComponent from 'htmlcomponent'
import sleep from '@/utils/sleep';
import {getUrlParams} from '@/utils/urlParams';

export default {
	mixins: [
		GenericMixin,
	],
	components: {
		ValidationProvider,
		ValidationObserver,
		FormInput,
		FormInputCheckbox,
		Button,
		GeneralMessage,
	},
	props: {
		confirm: {
			type: String,
			default: '',
		},
		token: {
			type: String,
			default: '',
		},
	},
	data() {
		return {
			form: {
				current: '',
				password: "",
				confirm: "",
				token: '',
			},
			resetResponse: {
				type: 'info',
				message: '',
			},
			confirmUrl: this.$root.options.confirmUrl || this.confirm,
		}
	},
	computed: {
		...mapGetters("account", [
			"isLoggedIn"
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		disabled() {
			return this.loading;
		},
		edited() {
			return !!this.form.current || !!this.form.password || !!this.form.confirm;
		}
	},
	methods: {
		async onReset() {
			const isValid = await this.$refs.resetForm.validate()
			if (isValid) {
				this.loading = true;
				document.activeElement.blur();
				try {
					const response = await this.$store.dispatch('account/resetPassword', this.form)
					if (response && response.redirect && response.redirect.key) {
						return this.$store.dispatch('redirect', {
							name: response.redirect.key,
							params: response.redirect.params || {},
						})
					}
					else if (this.confirmUrl) {
						return this.$store.dispatch('redirect', {
							name: 'custom',
							url: this.confirmUrl,
						});
					}
					this.resetForm();
					this.resetResponse.message = this.$t('account.resetPassword.success');
					this.resetResponse.type = 'success';
					this.$emit('resetted');
				} catch(err) {
					let message = this.$t('forms.errors.generic')
					this.isDebug() && console.error(err);
					const data = err && err.response && err.response.data;

					if (data) {
						if (data.redirect && data.redirect.key) {
							return this.$store.dispatch('redirect', {
								name: data.redirect.key,
								params: data.redirect.params || {},
							})
						}

						if (data.errors && data.errors.email) {
							message = data.errors.email[0]
						} else {
							message = data.message
						}
					}

					this.resetResponse.message = message;
					this.resetResponse.type = 'error';
				}
				this.loading = false;
			}
		},
		resetForm(form, inputs = true) {
			try {
				if (!form || form === 'reset') {
					if (inputs) {
						this.form.current = '';
						this.form.password = '';
						this.form.confirm = '';
					}
					this.resetResponse.message = '';
					this.$refs.resetForm.reset();
				}
				if (!form) {
					this.loading = false;
				}
				if (inputs) {
					document.activeElement.blur();
				}
			}
			catch(err) {}
		},
	},
	async mounted() {
		const params = getUrlParams();
		if (!params.token && !this.token) {
			this.error = this.$t('forms.errors.generic');
		}
		else {
			this.form.token = params.token || this.token;
		}
	}
}
</script>

<style>

</style>
